import { useState, useEffect, useCallback, useMemo } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import apiClient from '../../services/api/apiClient';
import { Spinner } from 'react-bootstrap';
import ClientSelect from '../../components/select/ClientSelect';
import { IClient } from '../../utils/types/IClient';
import { PriseDetail } from '../../utils/types/PriseDetail';
import formatNumber from '../../utils/formatters/formatNumber';
import { Carte } from '../../utils/types/Carte';
import { Helmet } from 'react-helmet-async';
import SkeletonLoader from '../../components/loader/SkeletonLoader';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DetailLivraison } from '../../utils/types/DetailLivraison';
import LivraisonTableSkeleton from '../../components/loading/livraison-table-skeleton';
import { ReglementClientDTO } from '../../utils/types/objects/ReglementClientDTO';
import PaiementTableSkeleton from '../../components/loading/paiement-table-skeleton';
import { SoldeClientDTO } from '../../utils/types/objects/SoldeClientDTO';
import { FavoritePointOfSaleDTO } from '../../utils/types/objects/FavoritePointOfSaleDTO';
import FavoritePointOfSaleTableSkeleton from '../../components/loading/favorite-pos-table-skeleton';
import SoldeCarteTableSkeleton from '../../components/loading/solde-carte-table-skeleton';
import LastDeliveriesGrid from '../../components/grid/dashboard/LastDeliveriesGrid';
import LastPaymentsGrid from '../../components/grid/dashboard/LastPaymentsGrid';
import FavoritePointsOfSaleGrid from '../../components/grid/dashboard/FavoritePointsOfSaleGrid';
import SoldesCartesGrid from '../../components/grid/dashboard/SoldesCartesGrid';
import AccordionCard from '../../components/card/accordion-card';
import { AnimatePresence, motion } from 'framer-motion';
import PrisesBarChart from '../../components/chart/prises-bar-chart';
import PrisesPieChart from '../../components/chart/prises-pie-chart';
import './index.css';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';

type PrisesData = Record<string, Record<string, PriseDetail>>;

const Home = () => {
    const currentYear = new Date().getFullYear();
    const annees: number[] = Array.from({ length: currentYear - 2024 + 1 }, (_, index) => 2024 + index);

    // ---

    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);
    const [selectedAnnee, setSelectedAnnee] = useState<number>(currentYear);

    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);

    // ---

    //#region Queries

    const queryClient = useQueryClient();

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: synthese,
        isError: isSyntheseError,
        error: syntheseError,
        refetch: loadSyntheseClient,
    } = useQuery<SoldeClientDTO>({
        queryKey: ['synthese-client', { codeClient: selectedCardCode }],

        queryFn: async ({ signal }) => {
            const response = await apiClient.get('/api/clients/get-solde-client', {
                params: { codeClient: selectedCardCode },
                signal,
            });
            return response.data;
        },

        meta: { title: 'Chargement de la synthèse client' },

        enabled: false,
        staleTime: 0,
        retry: 1,
    });

    const {
        data: prises,
        isLoading: isPrisesLoading,
        isError: isPrisesError,
        error: prisesError,
        refetch: loadPrises,
        isFetched: isPrisesFetched,
    } = useQuery<PrisesData>({
        queryKey: ['prisesDashboard', { codeClient: selectedCardCode, annee: selectedAnnee }],

        queryFn: async ({ signal }) => {
            const response = await apiClient.get('/api/prises/dashboard', {
                params: {
                    codeClient: selectedCardCode,
                    typePrise: null,
                    numeroCarte: null,
                    annee: selectedAnnee,
                },
                signal,
            });
            return response.data;
        },

        meta: { title: 'Chargement des prises' },

        enabled: false,
        staleTime: 1800000, // 30 min
        retry: 1,
    });

    const {
        data: lastDeliveries,
        isLoading: isLastDeliveriesLoading,
        isError: isLastDeliveriesError,
        error: lastDeliveriesError,
        refetch: loadLastDeliveries,
        isFetched: isLastDeliveriesFetched,
    } = useQuery<DetailLivraison[]>({
        queryKey: ['lastDeliveries', { codeClient: selectedCardCode }],
        queryFn: async () => {
            if (!selectedCardCode) return [];
            const response = await apiClient.get<DetailLivraison[]>('/api/prises/getData', {
                params: {
                    selectedCardCode: selectedCardCode,
                    lastDeliveries: true,
                    limit: 5,
                },
            });

            return response.data;
        },
        meta: { title: 'Chargement des dernières livraisons' },
        enabled: false,
        staleTime: 1800000, // 30 min
        retry: 1,
    });

    const {
        data: lastPayments,
        isLoading: isLastPaymentsLoading,
        isError: isLastPaymentsError,
        error: lastPaymentsError,
        refetch: loadLastPayments,
        isFetched: isLastPaymentsFetched,
    } = useQuery<ReglementClientDTO[]>({
        queryKey: ['lastPayments', { codeClient: selectedCardCode }],
        queryFn: async () => {
            if (!selectedCardCode) return [];
            const response = await apiClient.get<ReglementClientDTO[]>('/api/paiements/getData', {
                params: {
                    customerCardCode: selectedCardCode,
                    lastPayments: true,
                    limit: 5,
                },
            });

            return response.data;
        },
        meta: { title: 'Chargement des derniers paiements' },
        enabled: false,
        staleTime: 1800000,
        retry: 1,
    });

    const {
        data: favoritePointsOfSale,
        isLoading: isFavoritePointsOfSaleLoading,
        isError: isFavoritePointsOfSaleError,
        error: favoritePointsOfSaleError,
        refetch: loadFavoritePointsOfSale,
        isFetched: isFavoritePointsOfSaleFetched,
    } = useQuery<FavoritePointOfSaleDTO[]>({
        queryKey: ['favoritePointsOfSale', { codeClient: selectedCardCode }],
        queryFn: async () => {
            if (!selectedCardCode) return [];
            const response = await apiClient.get<FavoritePointOfSaleDTO[]>('/api/clients/get-favorite-points-of-sale', {
                params: {
                    codeClient: selectedCardCode,
                },
            });

            return response.data;
        },
        meta: { title: 'Chargement des stations les plus fréquentées' },
        enabled: false,
    staleTime: 1800000, //  30 min
        retry: 1,
    });

    const {
        data: soldesCartes,
        isLoading: isSoldesCartesLoading,
        isError: isSoldesCartesError,
        error: soldesCartesError,
        refetch: loadSoldesCartes,
        isFetched: isSoldesCartesFetched,
    } = useQuery<Carte[]>({
        queryKey: ['soldesCartes', { codeClient: selectedCardCode }],
        queryFn: async () => {
            if (!selectedCardCode) return [];
            const response = await apiClient.get<Carte[]>('/api/cartes/getData', {
                params: {
                    customerCardCode: selectedCardCode,
                    onlyActive: true,
                },
            });

            return response.data;
        },
        meta: { title: 'Chargement des cartes' },
        enabled: false,
        retry: 1,
        staleTime: 0
    });

    //#endregion

    const loadData = useCallback(async () => {
        if (!selectedCardCode) return;

        const fetchData = async () => {
            try {
                setIsDataLoading(true);

                await loadSyntheseClient();
                await loadPrises();

                await loadSoldesCartes();
                await loadLastDeliveries();
                await loadLastPayments();
                await loadFavoritePointsOfSale();
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, [selectedCardCode, setIsDataLoading, loadSyntheseClient, loadPrises, loadSoldesCartes, loadLastDeliveries, loadLastPayments, loadFavoritePointsOfSale]);

    //#region Effects

    useEffect(() => {
        if (clients.length === 1) {
            setSelectedCardCode(clients[0].cardCode);
        }
    }, [clients]);

    useEffect(() => {
        if (!selectedCardCode) return;
        loadData();
    }, [selectedCardCode, loadData]);

    useEffect(() => {
        if (!selectedCardCode) return;
        loadPrises();
    }, [selectedCardCode, loadPrises]);

    //#endregion

    // ---

    const handleCartesUpdated = () => {
        queryClient.refetchQueries({ queryKey: ['synthese-client', { codeClient: selectedCardCode }] });
        queryClient.refetchQueries({ queryKey: ['soldesCartes', { codeClient: selectedCardCode }] });

        loadSyntheseClient();
        loadSoldesCartes();
    };

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isSyntheseError, error: syntheseError, title: 'Erreur lors du chargement de la synthèse', refetch: loadSyntheseClient },
            { isError: isPrisesError, error: prisesError, title: 'Erreur lors du chargement des prises', refetch: loadPrises },
            { isError: isLastDeliveriesError, error: lastDeliveriesError, title: 'Erreur lors du chargement des dernières livraisons', refetch: loadLastDeliveries },
            { isError: isLastPaymentsError, error: lastPaymentsError, title: 'Erreur lors du chargement des derniers paiements', refetch: loadLastPayments },
            { isError: isFavoritePointsOfSaleError, error: favoritePointsOfSaleError, title: 'Erreur lors du chargement des stations les plus fréquentées', refetch: loadFavoritePointsOfSale },
            { isError: isSoldesCartesError, error: soldesCartesError, title: 'Erreur lors du chargement des soldes des cartes', refetch: loadSoldesCartes },
        ],
        [isClientsError, clientsError, refetchClients, isSyntheseError, syntheseError, loadSyntheseClient, isPrisesError, prisesError, loadPrises, isLastDeliveriesError, lastDeliveriesError, loadLastDeliveries, isLastPaymentsError, lastPaymentsError, loadLastPayments, isFavoritePointsOfSaleError, favoritePointsOfSaleError, loadFavoritePointsOfSale, isSoldesCartesError, soldesCartesError, loadSoldesCartes],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Accueil :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row align-items-center">
                        <div className="col-12 col-md-auto ms-auto d-print-none order-1 order-md-1">
                            <div>
                                <div className="row align-items-center">
                                    <div className="col-auto">
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-users-group">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" />
                                                    <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path d="M17 10h2a2 2 0 0 1 2 2v1" />
                                                    <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path d="M3 13v-1a2 2 0 0 1 2 -2h2" />
                                                </svg>
                                            </span>
                                            <ClientSelect clients={clients} isClientsLoading={isLoadingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} isDisabled={isDataLoading} />
                                        </div>
                                    </div>

                                    <div className="col-auto">
                                        <div className="input-group">
                                            <button type="button" className="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false" disabled={isDataLoading}>
                                                {selectedAnnee}
                                            </button>
                                            <div className="dropdown-menu">
                                                {annees.map((annee) => (
                                                    <button
                                                        key={annee}
                                                        className="dropdown-item"
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setSelectedAnnee(annee);
                                                        }}
                                                    >
                                                        {annee}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-auto">
                                        <div className="btn-group my-1">
                                            <button
                                                className="btn btn-6 w-100 btn-icon"
                                                aria-label="Actualiser"
                                                disabled={isDataLoading}
                                                type="button"
                                                onClick={() => {
                                                    loadData();
                                                }}
                                            >
                                                {isDataLoading ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                    </>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-refresh">
                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                        <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                                                        <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col order-0 order-md-0">
                            <h2 className="page-title">Tableau de bord</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body mt-1">
                <div className="container-xl">
                    <QueryErrorAlerts queries={queryErrors} />

                    <AnimatePresence>
                        {synthese && (
                            <motion.div key="box" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }} className="row my-3">
                                <div className="col-sm-6 col-md-4 col-lg-2">
                                    <div className="card card-sm">
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-auto">
                                                    <span className="bg-primary text-white avatar">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-credit-card-pay">
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M12 19h-6a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v4.5" />
                                                            <path d="M3 10h18" />
                                                            <path d="M16 19h6" />
                                                            <path d="M19 16l3 3l-3 3" />
                                                            <path d="M7.005 15h.005" />
                                                            <path d="M11 15h2" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div className="col">
                                                    <div className="font-weight-medium">{formatNumber(synthese.paiementPOS ?? 0, { style: 'currency', currency: 'USD' })}</div>
                                                    <div className="text-secondary">Paiements Station</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-6 col-md-4  col-lg-2">
                                    <div className="card card-sm">
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-auto">
                                                    <span className="bg-primary text-white avatar">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-credit-card-pay">
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M12 19h-6a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v4.5" />
                                                            <path d="M3 10h18" />
                                                            <path d="M16 19h6" />
                                                            <path d="M19 16l3 3l-3 3" />
                                                            <path d="M7.005 15h.005" />
                                                            <path d="M11 15h2" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div className="col">
                                                    <div className="font-weight-medium">{formatNumber(synthese.paiementSAP ?? 0, { style: 'currency', currency: 'USD' })}</div>
                                                    <div className="text-secondary">Paiements Banque</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-6 col-md-4  col-lg-2">
                                    <div className="card card-sm">
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-auto">
                                                    <span className="bg-primary text-white avatar">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-credit-card-pay">
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M12 19h-6a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v4.5" />
                                                            <path d="M3 10h18" />
                                                            <path d="M16 19h6" />
                                                            <path d="M19 16l3 3l-3 3" />
                                                            <path d="M7.005 15h.005" />
                                                            <path d="M11 15h2" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div className="col">
                                                    <div className="font-weight-medium">{formatNumber(synthese.totalPaiement ?? 0, { style: 'currency', currency: 'USD' })}</div>
                                                    <div className="text-secondary">Total Paiement</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-6 col-md-4  col-lg-2">
                                    <div className="card card-sm">
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-auto">
                                                    <span className="bg-primary text-white avatar">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-align-box-center-bottom">
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M3 19v-14a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" />
                                                            <path d="M11 17h2" />
                                                            <path d="M9 14h6" />
                                                            <path d="M10 11h4" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div className="col">
                                                    <div className="font-weight-medium">{selectedCardCode !== 'C5593' ? formatNumber(synthese.solde ?? 0, { style: 'currency', currency: 'USD' }) : '###'}</div>
                                                    <div className="text-secondary">Solde Client</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-6 col-md-4  col-lg-2">
                                    <div className="card card-sm">
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-auto">
                                                    <span className="bg-primary text-white avatar">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-rotate-2">
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M15 4.55a8 8 0 0 0 -6 14.9m0 -4.45v5h-5" />
                                                            <path d="M18.37 7.16l0 .01" />
                                                            <path d="M13 19.94l0 .01" />
                                                            <path d="M16.84 18.37l0 .01" />
                                                            <path d="M19.37 15.1l0 .01" />
                                                            <path d="M19.94 11l0 .01" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div className="col">
                                                    <div className="font-weight-medium">{selectedCardCode !== 'C5593' ? formatNumber(synthese.recharge ?? 0, { style: 'currency', currency: 'USD' }) : '###'}</div>
                                                    <div className="text-secondary">Recharge Cartes</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-6 col-md-4  col-lg-2">
                                    <div className="card card-sm">
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-auto">
                                                    <span className="bg-primary text-white avatar">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-gas-station">
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M14 11h1a2 2 0 0 1 2 2v3a1.5 1.5 0 0 0 3 0v-7l-3 -3" />
                                                            <path d="M4 20v-14a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v14" />
                                                            <path d="M3 20l12 0" />
                                                            <path d="M18 7v1a1 1 0 0 0 1 1h1" />
                                                            <path d="M4 11l10 0" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div className="col">
                                                    <div className="font-weight-medium">{formatNumber(synthese.livraisonsSansCarte ?? 0, { style: 'currency', currency: 'USD' })}</div>
                                                    <div className="text-secondary">Livr. Sans Carte</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="card my-3">
                        <div className="accordion accordion-flush" id="accordion-flush">
                            <div className="accordion-item">
                                <button className="accordion-header" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-1-flush" aria-expanded="true">
                                    <div className="accordion-header-text">
                                        <h4>Consommation carburant</h4>
                                    </div>
                                    <div className="accordion-header-toggle">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
                                            <path d="M6 9l6 6l6 -6"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="collapse-1-flush" className="accordion-collapse collapse show" data-bs-parent="#accordion-flush">
                                    <div className="accordion-body">
                                        {isPrisesLoading || isDataLoading ? (
                                            <>
                                                <p className="text-center">En cours de chargement</p>
                                                <SkeletonLoader />
                                            </>
                                        ) : !prises || Object.keys(prises).length === 0 ? (
                                            <>
                                                {isPrisesFetched ? (
                                                    <div className="alert alert-warning" role="alert">
                                                        <div className="alert-icon">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                                <path d="M12 9v4"></path>
                                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                                <path d="M12 16h.01"></path>
                                                            </svg>
                                                        </div>
                                                        Aucune donnée trouvée.
                                                    </div>
                                                ) : (
                                                    <div className="alert alert-info" role="alert">
                                                        <div className="alert-icon">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-alert-square-rounded">
                                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                <path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm.01 13l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007zm-.01 -8a1 1 0 0 0 -.993 .883l-.007 .117v4l.007 .117a1 1 0 0 0 1.986 0l.007 -.117v-4l-.007 -.117a1 1 0 0 0 -.993 -.883z" />
                                                            </svg>
                                                        </div>
                                                        Veuillez charger les données.
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="card card-body">
                                                        <PrisesBarChart prises={prises} height={230} />
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="card card-body">
                                                        <PrisesPieChart prises={prises} height={200} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hr-text">#</div>

                    <div className="row">
                        <div className="col-md-6">
                            <AccordionCard title="Soldes cartes" id="soldes">
                                {isSoldesCartesLoading || isDataLoading ? (
                                    <SoldeCarteTableSkeleton />
                                ) : soldesCartes && soldesCartes.length > 0 ? (
                                    <SoldesCartesGrid items={soldesCartes} />
                                ) : isSoldesCartesFetched ? (
                                    <div className="alert alert-warning" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                <path d="M12 9v4"></path>
                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                <path d="M12 16h.01"></path>
                                            </svg>
                                        </div>
                                        Aucune donnée trouvée.
                                    </div>
                                ) : (
                                    <div className="alert alert-info" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-alert-square-rounded">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm.01 13l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007zm-.01 -8a1 1 0 0 0 -.993 .883l-.007 .117v4l.007 .117a1 1 0 0 0 1.986 0l.007 -.117v-4l-.007 -.117a1 1 0 0 0 -.993 -.883z" />
                                            </svg>
                                        </div>
                                        Veuillez charger les données.
                                    </div>
                                )}
                            </AccordionCard>
                        </div>

                        <div className="col-md-6">
                            <AccordionCard title="5 dernières livraisons" id="livraisons">
                                {isLastDeliveriesLoading || isDataLoading ? (
                                    <LivraisonTableSkeleton />
                                ) : lastDeliveries && lastDeliveries.length > 0 ? (
                                    <LastDeliveriesGrid items={lastDeliveries} />
                                ) : isLastDeliveriesFetched ? (
                                    <div className="alert alert-warning" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                <path d="M12 9v4"></path>
                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                <path d="M12 16h.01"></path>
                                            </svg>
                                        </div>
                                        Aucune donnée trouvée.
                                    </div>
                                ) : (
                                    <div className="alert alert-info" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-alert-square-rounded">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm.01 13l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007zm-.01 -8a1 1 0 0 0 -.993 .883l-.007 .117v4l.007 .117a1 1 0 0 0 1.986 0l.007 -.117v-4l-.007 -.117a1 1 0 0 0 -.993 -.883z" />
                                            </svg>
                                        </div>
                                        Veuillez charger les données.
                                    </div>
                                )}
                            </AccordionCard>
                        </div>

                        <div className="col-md-6">
                            <AccordionCard title="5 derniers paiements" id="paiements">
                                {isLastPaymentsLoading || isDataLoading ? (
                                    <PaiementTableSkeleton />
                                ) : lastPayments && lastPayments.length > 0 ? (
                                    <LastPaymentsGrid items={lastPayments} />
                                ) : isLastPaymentsFetched ? (
                                    <div className="alert alert-warning" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                <path d="M12 9v4"></path>
                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                <path d="M12 16h.01"></path>
                                            </svg>
                                        </div>
                                        Aucune donnée trouvée.
                                    </div>
                                ) : (
                                    <div className="alert alert-info" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-alert-square-rounded">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm.01 13l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007zm-.01 -8a1 1 0 0 0 -.993 .883l-.007 .117v4l.007 .117a1 1 0 0 0 1.986 0l.007 -.117v-4l-.007 -.117a1 1 0 0 0 -.993 -.883z" />
                                            </svg>
                                        </div>
                                        Veuillez charger les données.
                                    </div>
                                )}
                            </AccordionCard>
                        </div>

                        <div className="col-md-6">
                            <AccordionCard title="5 stations les plus fréquentées" id="stations">
                                {isFavoritePointsOfSaleLoading || isDataLoading ? (
                                    <FavoritePointOfSaleTableSkeleton />
                                ) : favoritePointsOfSale && favoritePointsOfSale.length > 0 ? (
                                    <FavoritePointsOfSaleGrid items={favoritePointsOfSale} />
                                ) : isFavoritePointsOfSaleFetched ? (
                                    <div className="alert alert-warning" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                <path d="M12 9v4"></path>
                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                <path d="M12 16h.01"></path>
                                            </svg>
                                        </div>
                                        Aucune donnée trouvée.
                                    </div>
                                ) : (
                                    <div className="alert alert-info" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-alert-square-rounded">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm.01 13l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007zm-.01 -8a1 1 0 0 0 -.993 .883l-.007 .117v4l.007 .117a1 1 0 0 0 1.986 0l.007 -.117v-4l-.007 -.117a1 1 0 0 0 -.993 -.883z" />
                                            </svg>
                                        </div>
                                        Veuillez charger les données.
                                    </div>
                                )}
                            </AccordionCard>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

Home.Layout = DefaultLayout;

export default Home;
